import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { nativeFetch } from '../utils/nativeApi';

const FREQUENCIES = ['daily', 'weekly', 'biweekly', 'monthly', 'quarterly', 'yearly'];
const DAYS_OF_WEEK = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function RecurringPayments() {
  const { t } = useTranslation(['settings', 'common']);
  
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [categories, setCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    amount: '',
    currency: 'ILS',
    frequency: 'monthly',
    day_of_month: 1,
    day_of_week: 0,
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    category_id: '',
    payment_method_id: '',
    note: '',
  });

  useEffect(() => {
    fetchPayments();
    fetchCategories();
    fetchPaymentMethods();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchPayments = async () => {
    try {
      const response = await nativeFetch('/api/recurring-payments');
      if (response.ok) {
        const data = await response.json();
        setPayments(data);
      }
    } catch (error) {
      console.error('Error fetching recurring payments:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await nativeFetch('/api/categories');
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPaymentMethods = async () => {
    try {
      const response = await nativeFetch('/api/payment-methods');
      if (response.ok) {
        const data = await response.json();
        setPaymentMethods(data);
      }
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      amount: '',
      currency: 'ILS',
      frequency: 'monthly',
      day_of_month: 1,
      day_of_week: 0,
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      category_id: '',
      payment_method_id: '',
      note: '',
    });
    setEditingPayment(null);
  };

  const openCreateModal = () => {
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (payment) => {
    setEditingPayment(payment);
    setFormData({
      title: payment.title,
      amount: payment.amount,
      currency: payment.currency,
      frequency: payment.frequency,
      day_of_month: payment.day_of_month || 1,
      day_of_week: payment.day_of_week || 0,
      start_date: payment.start_date,
      end_date: payment.end_date || '',
      category_id: payment.category?.id || '',
      payment_method_id: payment.payment_method?.id || '',
      note: payment.note || '',
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const payload = {
        title: formData.title,
        amount: parseFloat(formData.amount),
        currency: formData.currency,
        frequency: formData.frequency,
        start_date: formData.start_date,
        end_date: formData.end_date || null,
        category_id: formData.category_id || null,
        payment_method_id: formData.payment_method_id || null,
        note: formData.note || null,
      };

      if (['monthly', 'quarterly', 'yearly'].includes(formData.frequency)) {
        payload.day_of_month = parseInt(formData.day_of_month);
      }
      if (['weekly', 'biweekly'].includes(formData.frequency)) {
        payload.day_of_week = parseInt(formData.day_of_week);
      }

      const url = editingPayment
        ? `/api/recurring-payments/${editingPayment.id}`
        : `/api/recurring-payments`;
      
      const response = await nativeFetch(url, {
        method: editingPayment ? 'PUT' : 'POST',
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        toast.success(editingPayment ? t('recurring.updated') : t('recurring.created'));
        fetchPayments();
        closeModal();
      } else {
        const error = await response.json();
        toast.error(error.error || t('common:messages.error'));
      }
    } catch (error) {
      console.error('Error saving recurring payment:', error);
      toast.error(t('common:messages.error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (paymentId) => {
    if (!window.confirm(t('recurring.confirmDelete'))) return;

    try {
      const response = await nativeFetch(`/api/recurring-payments/${paymentId}`, {
        method: 'DELETE',
      });

      if (response.ok) {
        toast.success(t('recurring.deleted'));
        setPayments(payments.filter(p => p.id !== paymentId));
      } else {
        toast.error(t('common:messages.error'));
      }
    } catch (error) {
      console.error('Error deleting recurring payment:', error);
      toast.error(t('common:messages.error'));
    }
  };

  const handleToggleActive = async (payment) => {
    try {
      const response = await nativeFetch(`/api/recurring-payments/${payment.id}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !payment.is_active }),
      });

      if (response.ok) {
        const updated = await response.json();
        setPayments(payments.map(p => p.id === payment.id ? updated : p));
        toast.success(updated.is_active ? t('recurring.activated') : t('recurring.paused'));
      }
    } catch (error) {
      console.error('Error toggling payment:', error);
    }
  };

  const formatFrequency = (freq) => {
    return t(`recurring.frequencies.${freq}`);
  };

  const formatNextRun = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString();
  };

  if (loading) {
    return <p className="text-gray-600">{t('common:messages.loading')}</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <p className="text-gray-600 text-sm">{t('recurring.description')}</p>
        </div>
        <button
          onClick={openCreateModal}
          className="px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 transition-opacity"
        >
          ➕ {t('recurring.add')}
        </button>
      </div>

      {payments.length === 0 ? (
        <div className="p-6 bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg text-center">
          <p className="text-gray-500">{t('recurring.empty')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {payments.map((payment) => (
            <div
              key={payment.id}
              className={`p-4 border-2 border-black rounded-lg ${payment.is_active ? 'bg-white' : 'bg-gray-100 opacity-60'}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{payment.category?.icon || '💰'}</span>
                  <div>
                    <h4 className="font-bold">{payment.title}</h4>
                    <p className="text-sm text-gray-600">
                      {payment.amount} {payment.currency} • {formatFrequency(payment.frequency)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-500">
                    {t('recurring.nextRun')}: {formatNextRun(payment.next_run_date)}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 mt-3 pt-3 border-t border-gray-200">
                <button
                  onClick={() => handleToggleActive(payment)}
                  className={`px-3 py-1 text-sm border-2 border-black rounded-lg font-bold ${payment.is_active ? 'bg-yellow-100' : 'bg-green-100'}`}
                >
                  {payment.is_active ? '⏸️ ' + t('recurring.pause') : '▶️ ' + t('recurring.resume')}
                </button>
                <button
                  onClick={() => openEditModal(payment)}
                  className="px-3 py-1 text-sm border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
                >
                  ✏️ {t('common:buttons.edit')}
                </button>
                <button
                  onClick={() => handleDelete(payment.id)}
                  className="px-3 py-1 text-sm border-2 border-red-500 text-red-500 rounded-lg font-bold hover:bg-red-50"
                >
                  🗑️
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
          <div className="card max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-black mb-4">
              {editingPayment ? t('recurring.edit') : t('recurring.add')}
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Title */}
              <div>
                <label className="block text-sm font-bold mb-1">{t('recurring.fields.title')}</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="input"
                  placeholder={t('recurring.fields.titlePlaceholder')}
                  required
                />
              </div>

              {/* Amount & Currency */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-bold mb-1">{t('recurring.fields.amount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    className="input"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold mb-1">{t('recurring.fields.currency')}</label>
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                    className="input"
                  >
                    <option value="ILS">ILS</option>
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="GEL">GEL</option>
                  </select>
                </div>
              </div>

              {/* Frequency */}
              <div>
                <label className="block text-sm font-bold mb-1">{t('recurring.fields.frequency')}</label>
                <select
                  value={formData.frequency}
                  onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                  className="input"
                >
                  {FREQUENCIES.map(freq => (
                    <option key={freq} value={freq}>{formatFrequency(freq)}</option>
                  ))}
                </select>
              </div>

              {/* Day of Month (for monthly/quarterly/yearly) */}
              {['monthly', 'quarterly', 'yearly'].includes(formData.frequency) && (
                <div>
                  <label className="block text-sm font-bold mb-1">{t('recurring.fields.dayOfMonth')}</label>
                  <select
                    value={formData.day_of_month}
                    onChange={(e) => setFormData({ ...formData, day_of_month: e.target.value })}
                    className="input"
                  >
                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                      <option key={day} value={day}>{day}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">{t('recurring.fields.dayOfMonthHint')}</p>
                </div>
              )}

              {/* Day of Week (for weekly/biweekly) */}
              {['weekly', 'biweekly'].includes(formData.frequency) && (
                <div>
                  <label className="block text-sm font-bold mb-1">{t('recurring.fields.dayOfWeek')}</label>
                  <select
                    value={formData.day_of_week}
                    onChange={(e) => setFormData({ ...formData, day_of_week: e.target.value })}
                    className="input"
                  >
                    {DAYS_OF_WEEK.map((day, idx) => (
                      <option key={idx} value={idx}>{day}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Start Date */}
              <div>
                <label className="block text-sm font-bold mb-1">{t('recurring.fields.startDate')}</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="input"
                  required
                  disabled={!!editingPayment}
                />
              </div>

              {/* End Date (optional) */}
              <div>
                <label className="block text-sm font-bold mb-1">{t('recurring.fields.endDate')}</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="input"
                  min={formData.start_date}
                />
                <p className="text-xs text-gray-500 mt-1">{t('recurring.fields.endDateHint')}</p>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-bold mb-1">{t('recurring.fields.category')}</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  className="input"
                >
                  <option value="">{t('recurring.fields.selectCategory')}</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Payment Method */}
              <div>
                <label className="block text-sm font-bold mb-1">{t('recurring.fields.paymentMethod')}</label>
                <select
                  value={formData.payment_method_id}
                  onChange={(e) => setFormData({ ...formData, payment_method_id: e.target.value })}
                  className="input"
                >
                  <option value="">{t('recurring.fields.selectPaymentMethod')}</option>
                  {paymentMethods.map(pm => (
                    <option key={pm.id} value={pm.id}>{pm.name}</option>
                  ))}
                </select>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-bold mb-1">{t('recurring.fields.note')}</label>
                <input
                  type="text"
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className="input"
                  placeholder={t('recurring.fields.notePlaceholder')}
                />
              </div>

              {/* Buttons */}
              <div className="flex gap-2 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-white hover:bg-gray-100"
                >
                  {t('common:buttons.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 border-2 border-black rounded-lg font-bold bg-primary shadow-hard-sm hover:opacity-90 disabled:opacity-50"
                >
                  {saving ? t('common:buttons.saving') : t('common:buttons.save')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default RecurringPayments;
